import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

// When Quote Builder is the active product, ALL legacy Paydaai screens
// (dashboard, payments, expenses, admin/*) must be inaccessible even to
// authenticated users. This flag is read at module load; changing it
// requires a rebuild (same as the rest of the CRA env pipeline).
const QUOTE_MODULE_ENABLED = process.env.REACT_APP_QUOTE_MODULE_ENABLED === 'true';

// Paths that ARE valid in Quote Builder mode — everything else is legacy
// and gets redirected to /quotes for authenticated non-superadmins.
const QUOTE_MODE_ALLOWED_PREFIXES = ['/quotes', '/quote-', '/company-settings', '/superadmin', '/frozen'];

export const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false, requiredFeature = null, allowWhenFrozen = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg" data-testid="loading-spinner">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Quote Builder mode: block legacy admin/user screens. Superadmins keep
  // full access; Quote Builder paths (`/quotes`, `/quote-*`) render as
  // normal. Everything else gets a hard redirect. The Quote Setup Gate
  // then verifies the tenant's `businesses.product` = "quote_builder".
  if (QUOTE_MODULE_ENABLED && user.role !== 'superadmin') {
    const path = location.pathname || '/';
    const inQuoteMode = QUOTE_MODE_ALLOWED_PREFIXES.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p));
    if (!inQuoteMode) {
      return <Navigate to="/quotes" replace />;
    }
  }

  if (adminOnly && user.role !== 'admin' && user.role !== 'staff' && user.role !== 'superadmin') {
    if (user.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  if (superAdminOnly && user.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Package feature check for admin routes
  if (requiredFeature && (user.role === 'admin' || user.role === 'staff') && user.package) {
    if (user.package[requiredFeature] === false) {
      return <Navigate to="/admin/dashboard" replace />;
    }
  }

  // Frozen account check
  if (user.is_frozen && user.role !== 'superadmin' && !allowWhenFrozen) {
    return <Navigate to="/frozen" replace />;
  }

  return children;
};
